import { useCallback, useMemo } from 'react'

import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

type ItemAction =
  | {
      id: string
      label: string
      onClick: () => void
      type?: 'primary' | 'secondary' | 'other'
    }
  | {
      type: 'separator'
    }

interface UseMyExpensesItemActionsProps {
  expensableGroupsEnabled: boolean
  employeeId: number
  handlers: {
    openAddToGroupModal: (expensables: Expensable[]) => void
    openRemoveFromGroupModal: (expensables: Expensable[]) => void
  }
}

const canAddToGroup = (expensable: Expensable, employeeId: number): boolean => {
  return expensable.status === 'draft' && !expensable.groupId && expensable.owner?.id === employeeId
}

const canRemoveFromGroup = (expensable: Expensable): boolean => {
  return expensable.status === 'draft' && !!expensable.groupId
}

export const useMyExpensesItemActions = ({
  expensableGroupsEnabled,
  employeeId,
  handlers,
}: UseMyExpensesItemActionsProps) => {
  const executeAction = useCallback(
    (actionId: string, expensable: Expensable) => {
      switch (actionId) {
        case 'add-to-group':
          handlers.openAddToGroupModal([expensable])
          break

        case 'remove-from-group':
          handlers.openRemoveFromGroupModal([expensable])
          break
      }
    },
    [handlers]
  )

  const getItemActions = useMemo(
    () =>
      (expensable: Expensable): ItemAction[] => {
        if (!expensableGroupsEnabled) {
          return []
        }

        const actions: ItemAction[] = []

        if (canAddToGroup(expensable, employeeId)) {
          actions.push({
            id: 'add-to-group',
            label: i18n.t('expenses_v2.buttons.add_to_group_v2'),
            onClick: () => executeAction('add-to-group', expensable),
          })
        }

        if (canRemoveFromGroup(expensable)) {
          actions.push({
            id: 'remove-from-group',
            label: i18n.t('expenses_v2.buttons.remove_from_group_v2'),
            onClick: () => executeAction('remove-from-group', expensable),
          })
        }

        return actions
      },
    [expensableGroupsEnabled, employeeId, executeAction]
  )

  return {
    getItemActions,
  }
}
